<script setup lang="ts">
import type { NewsListItem } from 'types/news';

const layout = useLayout();
const root = useRoot();
const culture = root.settings.metadata.culture;
const isHovering = ref<boolean>(false);
const internalIsFocused = ref(false);

const props = defineProps<{
  newsItem: NewsListItem;
}>();

const formattedDate = computed(() => {
  return new Date(props.newsItem.date).toLocaleDateString(culture, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
});
</script>

<template>
  <article
    v-if="newsItem"
    class="group relative z-0 flex min-h-[500px] w-full cursor-pointer flex-col justify-between overflow-clip rounded-lg p-8 outline transition-all duration-300 text-shadow-sm hover:shadow-2xl"
    :class="{ 'shadow-2xl outline-primary': internalIsFocused, 'shadow-md outline-transparent': !internalIsFocused }"
    @mouseenter="isHovering = true"
    @mouseleave="isHovering = false"
  >
    <NuxtLink
      class="absolute inset-0 z-50 h-full w-full"
      :to="handleRelativeUrl(newsItem.url)"
      @focusin="internalIsFocused = true"
      @focusout="internalIsFocused = false"
    />
    <figure class="absolute inset-0 size-full rounded-lg">
      <BaseImage
        v-if="newsItem.media?.url"
        class="size-full rounded-lg object-cover brightness-50 transition-all duration-300 ease-out md:group-hover:brightness-[0.4]"
        :class="{ 'brightness-[0.4]': internalIsFocused }"
        :image="newsItem.media"
        :modifiers="{
          mode: 'crop',
          center: `${newsItem?.media.focalPoint?.left},${newsItem?.media.focalPoint?.top}`
        }"
        :width="layout.isDesktop ? 410 : 340"
        height="500"
      />

      <div
        v-if="layout.isMobile"
        class="ease absolute bottom-0 grid gap-5 p-4 text-white transition-all duration-300"
      >
        <h4 class="flex items-center">
          {{ $t('General.ReadMore') }}
          <BaseIcon
            name="chevron-right"
            prefix="arrows"
            stroke-color="var(--color-white)"
          />
        </h4>
      </div>
    </figure>
    <div class="relative flex w-full justify-end text-white">
      <div
        v-if="newsItem.tags.length"
        class="flex flex-col flex-wrap items-end gap-2 text-sm text-main text-shadow-none"
      >
        <span
          v-for="(tag, index) in newsItem.tags.slice(0, 2)"
          :key="`${tag}-${index}`"
          class="inline-flex items-center justify-center rounded-full bg-gray-30 px-4 py-2 opacity-70"
        >
          {{ tag }}
        </span>
      </div>
    </div>
    <div
      v-if="formattedDate || newsItem.headline"
      class="relative w-full text-white"
      :class="{ 'mb-10': layout.isMobile }"
    >
      <p v-if="formattedDate">{{ formattedDate }}</p>
      <h3
        v-if="newsItem.headline"
        class="leading-[1.1em]"
      >
        {{ newsItem.headline }}
      </h3>
      <BaseTransitionExpand>
        <div
          v-if="!layout.isMobile && (isHovering || internalIsFocused)"
          class="text-white"
        >
          <div class="flex translate-y-4 justify-between">
            <h4 class="flex min-h-12 items-center">
              {{ $t('General.ReadMore') }}
              <BaseIcon
                name="chevron-right"
                prefix="arrows"
                stroke-color="var(--color-white)"
              />
            </h4>
          </div>
        </div>
      </BaseTransitionExpand>
    </div>
  </article>
</template>
